import React from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { SignalIcon } from '@heroicons/react/24/outline'
import type {
  FacilitatorLiveSessionCard,
  GetFacilitatorLiveSessionMonitorResponse
} from '../../../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { ControlTowerCard } from './ControlTowerCard'
import { ControlTowerSprintCallCard } from './ControlTowerSprintCallCard'
import { ControlTowerLoopDetailsModal } from './ControlTowerLoopDetailsModal'

interface FacilitatorControlTowerProps {
  data?: GetFacilitatorLiveSessionMonitorResponse
  isLoading?: boolean
  error?: string | null
}

const CARDS_PER_ROW = 2

export const FacilitatorControlTower: React.FC<FacilitatorControlTowerProps> = ({ data, isLoading = false, error }) => {
  const { t } = useTranslation()
  const sections = data?.sections || []
  const [selectedCard, setSelectedCard] = React.useState<FacilitatorLiveSessionCard | null>(null)

  return (
    <>
      <Box
        data-testid="control-tower"
        sx={{
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderRadius: '20px',
          border: `1px solid ${INTERAX_COLORS.white10}`,
          p: { xs: 2, sm: 2.5, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          mb: 4
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 600, fontSize: '24px', lineHeight: '32px' }}>
              {t('facilitator.controlTower.title', 'Control Tower')}
            </Typography>
            <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: '14px', lineHeight: '20px' }}>
              {t('facilitator.controlTower.subtitle', 'Live view of active game sessions and AI debriefs.')}
            </Typography>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: INTERAX_COLORS.sun }} />
              <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: '14px' }}>
                {t('facilitator.controlTower.loading', 'Refreshing live status...')}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.75,
                borderRadius: '999px',
                backgroundColor: INTERAX_COLORS.white10
              }}
            >
              <SignalIcon style={{ width: 14, height: 14, color: INTERAX_COLORS.sun }} />
              <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: '12px', fontWeight: 600 }}>
                {t('facilitator.controlTower.liveBadge', 'LIVE VIEW')}
              </Typography>
            </Box>
          )}
        </Box>

        {error ? (
          <Box
            data-testid="control-tower-error"
            sx={{
              borderRadius: '14px',
              px: 2,
              py: 1.5,
              backgroundColor: INTERAX_COLORS.redErrorBackground,
              border: `1px solid ${INTERAX_COLORS.redError}`
            }}
          >
            <Typography sx={{ color: INTERAX_COLORS.redError, fontWeight: 600, mb: 0.5 }}>
              {t('facilitator.controlTower.errorTitle', 'Live monitor unavailable')}
            </Typography>
            <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: '14px' }}>
              {error || t('facilitator.controlTower.errorBody', 'Please try again in a moment.')}
            </Typography>
          </Box>
        ) : null}

        {!error && sections.length === 0 ? (
          <Box
            data-testid="control-tower-empty"
            sx={{
              borderRadius: '16px',
              px: 2.5,
              py: 3,
              backgroundColor: INTERAX_COLORS.white04,
              border: `1px dashed ${INTERAX_COLORS.white20}`,
              textAlign: 'center'
            }}
          >
            <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 600, mb: 0.5 }}>
              {t('facilitator.controlTower.emptyTitle', 'No live sessions right now')}
            </Typography>
            <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: '14px' }}>
              {t('facilitator.controlTower.emptyBody', 'Switch back to All activities to review the schedule.')}
            </Typography>
          </Box>
        ) : null}

        {!error &&
          sections.map((section) => {
            const cardCount = section.cards.length + section.sprintCalls.length
            const cardCountLabel =
              cardCount === 1
                ? t('facilitator.controlTower.cardCountOne', '1 card')
                : t('facilitator.controlTower.cardCountMany', '{{count}} cards', { count: cardCount })

            return (
              <Box key={section.sprintId} data-testid={`control-tower-section-${section.sprintId}`}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                  <Typography
                    sx={{ color: INTERAX_COLORS.white, fontWeight: 600, fontSize: '18px', lineHeight: '28px' }}
                  >
                    {section.sprintTitle}
                  </Typography>
                  <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: '13px' }}>{cardCountLabel}</Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr',
                      md: '1fr',
                      lg: `repeat(${CARDS_PER_ROW}, minmax(0, 1fr))`
                    },
                    gap: 1.5
                  }}
                >
                  {section.sprintCalls.map((sprintCall) => (
                    <ControlTowerSprintCallCard key={sprintCall.cardId} sprintCall={sprintCall} />
                  ))}
                  {section.cards.map((card) => (
                    <ControlTowerCard key={card.cardId} card={card} onOpenLoopDetails={setSelectedCard} />
                  ))}
                </Box>
              </Box>
            )
          })}
      </Box>

      <ControlTowerLoopDetailsModal card={selectedCard} onClose={() => setSelectedCard(null)} />
    </>
  )
}
